import React, { useState, useEffect } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { Wallet as WalletIcon, ArrowUp, ArrowDown, Minus } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatMoney } from '../utils/formatMoney';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/orbit`;

const OrbitWalletPage = () => {
  const { language } = useLanguage();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  const t = (fr, en) => language === 'fr' ? fr : en;

  useEffect(() => { fetchWallet(); }, []);

  const fetchWallet = async () => {
    try {
      const token = localStorage.getItem('orbit-token');
      const res = await axios.get(`${API}/wallet`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setWallet(res.data);
    } catch { toast.error(t('Erreur de chargement', 'Loading error')); }
    finally { setLoading(false); }
  };

  const typeLabels = {
    matrix: { fr: 'Commission Matrice', en: 'Matrix Commission', color: '#6366F1' },
    fast_start: { fr: 'Fast Start', en: 'Fast Start', color: '#10B981' },
    matching: { fr: 'Matching Bonus', en: 'Matching Bonus', color: '#8B5CF6' },
  };

  if (loading) {
    return (
      <OrbitLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </OrbitLayout>
    );
  }

  const totalIn = (wallet?.total_matrix_earnings || 0) + (wallet?.total_fast_start_earnings || 0) + (wallet?.total_matching_earnings || 0);
  const totalOut = wallet?.total_withdrawals || 0;

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="orbit-wallet-page">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('Portefeuille Orbit', 'Orbit Wallet')}</h1>

        {/* Balance Card */}
        <div className="bg-gradient-to-br from-indigo-600/30 to-purple-600/20 border border-indigo-500/20 rounded-2xl p-6">
          <p className="text-indigo-200 text-sm">{t('Solde disponible', 'Available Balance')}</p>
          <p className="text-4xl font-bold text-white mt-2" data-testid="orbit-wallet-balance">
            {formatMoney(wallet?.balance || 0, language)}
          </p>
        </div>

        {/* Entrees / Sorties / Solde */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-[#111827]/80 border border-emerald-500/20 rounded-xl p-4 flex sm:flex-col items-center sm:items-center gap-3 sm:gap-0 sm:text-center">
            <div className="flex items-center gap-2 sm:mb-2">
              <ArrowDown className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-gray-300">{t('Entrees', 'Income')}</span>
            </div>
            <p className="text-lg sm:text-xl font-bold text-emerald-400 ml-auto sm:ml-0 whitespace-nowrap" data-testid="orbit-wallet-income">{formatMoney(totalIn, language)}</p>
          </div>
          <div className="bg-[#111827]/80 border border-red-500/20 rounded-xl p-4 flex sm:flex-col items-center sm:items-center gap-3 sm:gap-0 sm:text-center">
            <div className="flex items-center gap-2 sm:mb-2">
              <ArrowUp className="w-4 h-4 text-red-400" />
              <span className="text-xs text-gray-400">{t('Sorties', 'Expenses')}</span>
            </div>
            <p className="text-lg sm:text-xl font-bold text-red-400 ml-auto sm:ml-0 whitespace-nowrap" data-testid="orbit-wallet-expenses">{formatMoney(totalOut, language)}</p>
          </div>
          <div className="bg-[#111827]/80 border border-indigo-500/20 rounded-xl p-4 flex sm:flex-col items-center sm:items-center gap-3 sm:gap-0 sm:text-center">
            <div className="flex items-center gap-2 sm:mb-2">
              <Minus className="w-4 h-4 text-indigo-400" />
              <span className="text-xs text-gray-300">{t('Solde', 'Balance')}</span>
            </div>
            <p className="text-lg sm:text-xl font-bold text-white ml-auto sm:ml-0 whitespace-nowrap">{formatMoney(totalIn - totalOut, language)}</p>
          </div>
        </div>

        {/* Earnings Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: t('Commissions Matrice', 'Matrix Commissions'), value: wallet?.total_matrix_earnings || 0, color: '#6366F1' },
            { label: 'Fast Start', value: wallet?.total_fast_start_earnings || 0, color: '#10B981' },
            { label: 'Matching Bonus', value: wallet?.total_matching_earnings || 0, color: '#8B5CF6' },
          ].map((item) => (
            <div key={item.label} className="bg-[#111827]/60 border rounded-xl p-4" style={{ borderColor: item.color + '30' }}>
              <p className="text-gray-300 text-sm">{item.label}</p>
              <p className="text-2xl font-bold text-white mt-1">{formatMoney(item.value, language)}</p>
              <div className="mt-2 h-1 rounded-full bg-gray-800">
                <div className="h-full rounded-full transition-all" style={{
                  backgroundColor: item.color,
                  width: totalIn > 0 ? `${(item.value / totalIn * 100)}%` : '0%'
                }} />
              </div>
            </div>
          ))}
        </div>

        {/* Recent Commissions */}
        <div className="bg-[#111827]/80 border border-indigo-500/10 rounded-xl">
          <div className="p-4 border-b border-indigo-500/10">
            <h2 className="text-lg font-semibold text-white">{t('Dernieres commissions', 'Recent Commissions')}</h2>
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {(wallet?.commissions || []).length === 0 ? (
              <p className="text-gray-500 text-center py-8">{t('Aucune commission', 'No commissions')}</p>
            ) : (
              <div className="divide-y divide-indigo-500/10">
                {(wallet?.commissions || []).map((comm) => {
                  const info = typeLabels[comm.type] || { fr: comm.type, en: comm.type, color: '#6366F1' };
                  return (
                    <div key={comm.commission_id || `${comm.from_user_id}-${comm.created_at}`} className="flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: info.color }} />
                        <div>
                          <p className="text-sm text-white">{language === 'fr' ? info.fr : info.en}</p>
                          <p className="text-xs text-gray-400">
                            {t('De', 'From')}: {comm.from_user_name} | {t('Orbite', 'Orbit')} {comm.orbit}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-emerald-400">+{formatMoney(comm.amount, language)}</p>
                        <p className="text-xs text-gray-400">{new Date(comm.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </OrbitLayout>
  );
};

export default OrbitWalletPage;
